import { useState, type FormEvent } from "react"
import { useForm, usePage } from "@inertiajs/react"

import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { FieldError, errorMessage } from "@/components/ui/field-error"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { ResponsiveDialog } from "@/components/ui/responsive-dialog"
import { StatusBadge } from "@/components/ui/status-badge"
import { Switch } from "@/components/ui/switch"
import { LocationSectionScreen } from "@/features/staff/location-section-screen"
import { t } from "@/lib/i18n"
import { patchInline, toastInlineUpdateError, toastInlineUpdateSuccess } from "@/lib/inline-update"
import { publicMenuState, publicMenuTone } from "@/lib/status-colors"

const tSection = (key: string) =>
  t(`ui.react.pages.staff.location.manage.location.sections.${key}`)

/**
 * public_menu has its own screen rather than a row among the feature switches
 * (decision 13) because it carries configuration, not just a boolean. The
 * screen is never reached at all when the platform has not granted the
 * feature — the controller redirects — so `available` is always true here.
 */
type PublicMenu = {
  available: boolean
  enabled: boolean
  slug: string | null
  url: string | null
  // The host *and path prefix* a slug resolves under (`host/r/`, no protocol),
  // always present even before a slug is chosen. Built server-side -- the
  // server is the only place that knows this, so the client never re-derives
  // it from window.location. It carries `/r/` because the form below shows the
  // URL by plain concatenation with the slug, so the prefix is the whole
  // answer to "where does this live".
  urlPrefix: string
  published: boolean
  suggestedSlug: string | null
}

type Props = {
  publicMenu: PublicMenu
  errors?: Record<string, string | string[]>
}

function PublicMenuCard({
  publicMenu,
  errors,
}: {
  publicMenu: PublicMenu
  errors: Record<string, string | string[]>
}) {
  const hasExistingSlug = Boolean(publicMenu.slug)
  const { data, setData, patch, processing } = useForm({
    slug: publicMenu.slug ?? publicMenu.suggestedSlug ?? "",
  })
  // Reclaiming a live slug is a collision-handling workflow, not a boolean
  // flip (interface-design skill: POST/create-shaped forms keep an explicit
  // submit). Confirmation only gates an actual CHANGE to an already-published
  // slug — the first slug a location ever claims has nothing to break yet.
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [togglingSwitch, setTogglingSwitch] = useState(false)

  const prefix = publicMenu.urlPrefix

  const submitSlug = () => {
    patch("/manage/location/public_menu/slug", {
      preserveScroll: true,
      onSuccess: () => setConfirmOpen(false),
    })
  }

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault()
    if (hasExistingSlug && data.slug !== publicMenu.slug) {
      setConfirmOpen(true)
      return
    }
    submitSlug()
  }

  // Decision 14: enabling the switch records intent, it does not require a
  // slug. It is a light boolean flip like every other feature switch, so it
  // commits the same way — patchInline, no submit button — and keeps the
  // testid the Playwright suite already depends on, unchanged by the move to
  // this card.
  const toggleEnabled = (enabled: boolean) => {
    setTogglingSwitch(true)
    patchInline("/manage/location/features/public_menu", { enabled })
      .then((result) => toastInlineUpdateSuccess(result))
      .catch((error: unknown) => toastInlineUpdateError(error))
      .finally(() => setTogglingSwitch(false))
  }

  return (
    <Card>
      <CardContent className="space-y-6">
        <form onSubmit={handleSubmit} className="space-y-2" data-testid="public-menu-slug-form">
          <Label htmlFor="public_menu_slug">{tSection("public_menu_slug_label")}</Label>
          <Input
            id="public_menu_slug"
            data-testid="public-menu-slug-input"
            startAdornment={prefix}
            value={data.slug}
            onChange={(event) => setData("slug", event.target.value)}
            aria-invalid={Boolean(errorMessage(errors.slug)) || undefined}
          />
          <p className="truncate text-xs text-muted-foreground" data-testid="public-menu-resulting-url">
            {data.slug ? `${prefix}${data.slug}` : tSection("public_menu_url_empty")}
          </p>
          <FieldError error={errors.slug} />
          <Button type="submit" size="sm" disabled={processing} data-testid="public-menu-slug-submit">
            {t("ui.common.actions.save")}
          </Button>
        </form>

        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0 space-y-1">
            <Label htmlFor="public_menu_enabled" className="text-sm font-medium">
              {tSection("public_menu_switch_label")}
            </Label>
            <p className="text-xs text-muted-foreground">{tSection("public_menu_switch_description")}</p>
          </div>
          <Switch
            id="public_menu_enabled"
            checked={publicMenu.enabled}
            disabled={togglingSwitch}
            onCheckedChange={toggleEnabled}
            data-testid="location-feature-public_menu-switch"
          />
        </div>
      </CardContent>

      {/* Keyed on confirmOpen so it remounts on every open/close: ResponsiveDialog
          only seeds its internal open state from the `open` prop once (see
          system-design.md's "Don't use ResponsiveDialog for a controlled drawer"
          note), so a second confirmation later in the session would otherwise
          never reopen once the first one closed. */}
      <ResponsiveDialog
        key={confirmOpen ? "public-menu-slug-confirm-open" : "public-menu-slug-confirm-closed"}
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title={tSection("public_menu_slug_change_title")}
        description={tSection("public_menu_slug_change_description")}
      >
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button
            type="button"
            variant="outline"
            onClick={() => setConfirmOpen(false)}
            data-testid="public-menu-slug-confirm-cancel"
          >
            {t("ui.common.actions.cancel")}
          </Button>
          <Button
            type="button"
            onClick={submitSlug}
            disabled={processing}
            data-testid="public-menu-slug-confirm-submit"
          >
            {tSection("public_menu_slug_change_confirm")}
          </Button>
        </div>
      </ResponsiveDialog>
    </Card>
  )
}


export default function StaffLocationManageLocationPublicMenu() {
  const { publicMenu, errors = {} } = usePage<Props>().props
  const state = publicMenuState(publicMenu)

  return (
    <LocationSectionScreen
      title={tSection("public_menu_title")}
      description={tSection("public_menu_description")}
      actions={<StatusBadge tone={publicMenuTone(state)} label={tSection(`public_menu_state_${state}`)} />}
    >
      <PublicMenuCard publicMenu={publicMenu} errors={errors} />
    </LocationSectionScreen>
  )
}
